import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { EASE, FadeIn, MaskedLine, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  "Brand Launch",
  "Corporate Event",
  "Private Event",
  "Other",
];

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  event_type: "",
  event_date: "",
  location: "",
  message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  const input =
    "w-full border-b border-ivory/25 bg-transparent py-3 font-light text-ivory placeholder:text-ivory/35 transition-colors duration-500 focus:border-pastel focus:outline-none";
  const label =
    "mb-1 block text-[10px] font-medium uppercase tracking-[0.35em] text-ivory/60";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-olive py-32 text-ivory sm:py-44"
    >
      <div className="mx-auto grid max-w-[1600px] gap-20 px-6 sm:px-10 lg:grid-cols-2 lg:gap-28 lg:px-16">
        <div className="relative">
          <FadeIn>
            <Eyebrow light>Enquiries</Eyebrow>
          </FadeIn>
          <h2 className="mt-10 font-serif font-light leading-[1.05] text-[clamp(1.8rem,4.2vw,3.85rem)]">
            <MaskedLine delay={0.1}>LET'S CREATE</MaskedLine>
            <MaskedLine delay={0.25}>
              SOMETHING <span className="italic text-pastel">BEAUTIFUL.</span>
            </MaskedLine>
          </h2>
          <FadeIn delay={0.3}>
            <div className="mt-16 space-y-8">
              <div>
                <p className={label}>Email</p>
                <a
                  href="mailto:hello@soraeventshouse.com"
                  data-testid="contact-email-link"
                  className="font-serif text-xl font-light italic text-ivory transition-colors duration-500 hover:text-pastel sm:text-2xl"
                >
                  hello@soraeventshouse.com
                </a>
              </div>
              <div>
                <p className={label}>Phone</p>
                <a
                  href="tel:+971567291129"
                  data-testid="contact-phone-link"
                  className="font-serif text-xl font-light italic text-ivory transition-colors duration-500 hover:text-pastel sm:text-2xl"
                >
                  +971 56 729 1129
                </a>
              </div>
              <div>
                <p className={label}>Studio</p>
                <p className="font-serif text-xl font-light italic text-ivory sm:text-2xl">
                  Dubai, UAE
                </p>
              </div>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.2} className="lg:pt-24">
          <AnimatePresence mode="wait">
            {status === "done" ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: EASE }}
                data-testid="enquiry-success"
                className="flex h-full min-h-[420px] flex-col justify-center"
              >
                <p className="font-serif text-5xl font-light italic sm:text-6xl">
                  Thank you.
                </p>
                <p className="mt-6 max-w-md font-light leading-relaxed text-ivory/80">
                  Your enquiry has been received. Bijal and the team will be in
                  touch with you shortly.
                </p>
                <button
                  onClick={() => {
                    setForm(EMPTY);
                    setStatus("idle");
                  }}
                  data-testid="enquiry-reset-button"
                  className="mt-10 w-fit border-b border-ivory/40 pb-1 text-[11px] font-semibold uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:border-pastel hover:text-pastel"
                >
                  Send another enquiry
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={submit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                data-testid="enquiry-form"
                className="grid grid-cols-1 gap-x-10 gap-y-9 sm:grid-cols-2"
              >
                <div>
                  <label className={label} htmlFor="enquiry-name">Name</label>
                  <input
                    id="enquiry-name"
                    data-testid="enquiry-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your full name"
                    className={input}
                  />
                </div>
                <div>
                  <label className={label} htmlFor="enquiry-email">Email</label>
                  <input
                    id="enquiry-email"
                    data-testid="enquiry-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className={input}
                  />
                </div>
                <div>
                  <label className={label} htmlFor="enquiry-phone">Phone</label>
                  <input
                    id="enquiry-phone"
                    data-testid="enquiry-phone-input"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+971 ..."
                    className={input}
                  />
                </div>
                <div className="relative">
                  <label className={label} htmlFor="enquiry-type">Event Type</label>
                  <select
                    id="enquiry-type"
                    data-testid="enquiry-type-select"
                    required
                    value={form.event_type}
                    onChange={set("event_type")}
                    style={{ colorScheme: "dark" }}
                    className={`${input} appearance-none ${form.event_type ? "" : "text-ivory/35"}`}
                  >
                    <option value="" disabled>
                      Select a celebration
                    </option>
                    {EVENT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={15}
                    className="pointer-events-none absolute bottom-4 right-1 text-ivory/50"
                  />
                </div>
                <div>
                  <label className={label} htmlFor="enquiry-date">Event Date</label>
                  <input
                    id="enquiry-date"
                    data-testid="enquiry-date-input"
                    type="date"
                    value={form.event_date}
                    onChange={set("event_date")}
                    style={{ colorScheme: "dark" }}
                    className={input}
                  />
                </div>
                <div>
                  <label className={label} htmlFor="enquiry-location">Location</label>
                  <input
                    id="enquiry-location"
                    data-testid="enquiry-location-input"
                    value={form.location}
                    onChange={set("location")}
                    placeholder="Dubai, or elsewhere"
                    className={input}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="enquiry-message">
                    Tell us about your event
                  </label>
                  <textarea
                    id="enquiry-message"
                    data-testid="enquiry-message-textarea"
                    required
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="The occasion, the atmosphere you imagine, the guest count..."
                    className={`${input} resize-none`}
                  />
                </div>
                {status === "error" && (
                  <p
                    data-testid="enquiry-error"
                    className="text-sm font-light text-pastel sm:col-span-2"
                  >
                    Something went wrong — please email us directly at
                    hello@soraeventshouse.com.
                  </p>
                )}
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="enquiry-submit-button"
                    className="group mt-4 inline-flex items-center gap-4 rounded-full bg-pastel px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-ink transition-colors duration-500 hover:bg-butter disabled:opacity-60"
                  >
                    {status === "sending" ? "Sending…" : "Begin Your Enquiry"}
                    <ArrowRight
                      size={15}
                      strokeWidth={1.75}
                      className="transition-transform duration-500 group-hover:translate-x-1.5"
                    />
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </FadeIn>
      </div>
    </section>
  );
};
